import {
  actionFromJSON,
  parseFrame,
  FrameVal,
  MixedMediaObjectType,
} from "./FlowObject";
import Color from "./Color";
import FontAttrsArray, { Alignment } from "./FontAttrsArray";
import { fontList } from "./FontList";
import {
  getCurrentDate,
  guidGenerator,
} from "../features/utils/commonFunctions";
import FontArray from "./FontAttrsArray";
import _cloneDeep from "lodash.clonedeep";
import GalleryAsset from "./GalleryAsset";
import ImageAsset, { PdfAsset } from "./Assets";
import Action from "./Action";
import { Interpreter } from "xstate";
import {
  FlowObjects,
  ChildMachineSchema,
  ChildEvents,
  EventKind,
  Kind,
  IndexName,
} from "../features/fsm/types";
import { UploadResponse } from "./Screens";

export default class TextFlowObject implements Kind {
  id: string;
  borderColor: Color;
  rotation: 0 | 1 | 2 | 3;
  galleryAssets: GalleryAsset | null;
  updated: string;
  frame: FrameVal;
  videoAsset: ImageAsset | null;
  zoomScale: number;
  retinaImageAsset: ImageAsset | null;
  pdfAsset: PdfAsset | null;
  type: MixedMediaObjectType;
  action: Action | null;
  originalImageAsset: ImageAsset | null;
  borderWidth: number;
  zoomOffset: {
    height: number;
    width: number;
  };
  audioAsset: ImageAsset | null;
  croppedImageAsset: ImageAsset | null;
  created: string;
  locked: boolean;
  /**
   * Hidden from JSON.parse, this informs video/pdf objects to only use previewImage
   */
  __forceUseImage: boolean;
  /**
   * Associated {@link MediaMachine} for this object
   */
  __ref?: Interpreter<
    { mmo: FlowObjects },
    ChildMachineSchema,
    ChildEvents,
    any
  >;
  isThemeText: boolean;
  textColor: Color;
  attributedString_fontArray: FontAttrsArray[];
  attributedString_rtf: string | null;
  textAlignment: Alignment;
  font: {
    fontName: string;
    fontSize: number;
  };
  attributedString_html: string | null;
  text: string;
  attributedString_flbAttributesArray: FontAttrsArray[];

  constructor(
    id: string,
    borderColor: Color,
    rotation: 0 | 1 | 2 | 3,
    galleryAssets: GalleryAsset | null,
    updated: string,
    frame: FrameVal,
    videoAsset: ImageAsset | null,
    zoomScale: number,
    retinaImageAsset: ImageAsset | null,
    pdfAsset: PdfAsset | null,
    type: MixedMediaObjectType,
    action: Action | null,
    originalImageAsset: ImageAsset | null,
    borderWidth: number,
    zoomOffset: {
      height: number;
      width: number;
    },
    audioAsset: ImageAsset | null,
    croppedImageAsset: ImageAsset | null,
    created: string,
    locked: boolean,
    isThemeText: boolean,
    textColor: Color,
    attributedString_fontArray: FontArray[],
    attributedString_rtf: string | null,
    textAlignment: Alignment,
    font: {
      fontName: string;
      fontSize: number;
    },
    attributedString_html: string | null,
    text: string,
    attributedString_flbAttributesArray: FontAttrsArray[],
  ) {
    this.id = id;
    this.borderColor = borderColor;
    this.rotation = rotation;
    this.galleryAssets = galleryAssets;
    this.updated = updated;
    this.frame = frame;
    this.videoAsset = videoAsset;
    this.zoomScale = zoomScale;
    this.retinaImageAsset = retinaImageAsset;
    this.pdfAsset = pdfAsset;
    this.type = type;
    this.action = action;
    this.originalImageAsset = originalImageAsset;
    this.borderWidth = borderWidth;
    this.zoomOffset = zoomOffset;
    this.audioAsset = audioAsset;
    this.croppedImageAsset = croppedImageAsset;
    this.created = created;
    this.locked = locked;
    this.__forceUseImage = false;
    this.isThemeText = isThemeText;
    this.textColor = textColor;
    this.attributedString_fontArray = attributedString_fontArray;
    this.attributedString_rtf = attributedString_rtf;
    // @ts-ignore
    this.textAlignment = Alignment[textAlignment];
    this.font = font;
    this.attributedString_html = attributedString_html;
    this.text = text;
    this.attributedString_flbAttributesArray = attributedString_flbAttributesArray;
  }

  eventKind(): EventKind {
    return EventKind.Screen;
  }

  setMMOUrl(response: UploadResponse[], indexMap: Map<IndexName, number>) {
    console.log("noop");
  }

  static fromJSON(json: any): TextFlowObject {
    if (typeof json === "string") {
      // if it's a string, parse it first
      return JSON.parse(json, TextFlowObject.reviver);
    }
    let flowObj = Object.create(TextFlowObject.prototype);
    Object.assign(flowObj, json, {
      textColor: Color.fromJSON(json.textColor),
      attributedString_fontArray: (json.attributedString_fontArray || []).map(
        (j: any) => FontAttrsArray.fromJSON(j)
      ),
      attributedString_flbAttributesArray: (json.attributedString_flbAttributesArray || []).map(
        (j: any) => FontAttrsArray.fromJSON(j)
      ),
      attributedString_rtf: null,
      attributedString_html: null,
      action: actionFromJSON(json.action),
      frame: parseFrame(json.frame),
    });
    return flowObj;
  }

  static reviver(key: string, value: any): any {
    return key === "" ? TextFlowObject.fromJSON(value) : value;
  }

  changeFontName(newFont: string): void {
    let fontLookup: string | undefined = fontList.find((x) => x === newFont);
    if (fontLookup !== undefined) {
      this.attributedString_fontArray[0].name = fontLookup;
      this.attributedString_flbAttributesArray[0].name = fontLookup;
      this.font.fontName = fontLookup;
    }
  }

  changeFontSize(newSize: number): void {
    this.attributedString_fontArray[0].size = newSize;
    this.attributedString_flbAttributesArray[0].size = newSize;
    this.font.fontSize = newSize;
  }

  changeColor(color: Color): void {
    this.textColor = color;
    this.attributedString_flbAttributesArray[0].color = color;
  }

  changeAlignment(align: Alignment): void {
    this.attributedString_flbAttributesArray[0].alignment = align;
    this.textAlignment = align;
  }

  setHeight(position: number): void {
    this.frame.height = position;
  }

  setWidth(position: number): void {
    this.frame.width = position;
  }

  overrideAttrs(vals: FontArray): TextFlowObject {
    console.log(vals);
    if (vals.alignment) this.attributedString_flbAttributesArray[0] = vals;
    const { size, name, color, alignment, range } = vals;
    this.textAlignment = alignment!;
    this.textColor = color!;
    this.font.fontName = name;
    this.font.fontSize = size;
    let cpy = _cloneDeep(vals);
    cpy.alignment = null;
    cpy.color = null;
    cpy.name = name;
    cpy.size = size;
    cpy.range = range;
    this.attributedString_fontArray[0] = cpy;
    console.log(this.attributedString_flbAttributesArray);
    return this;
  }

  setRange(length: number): void {
    this.attributedString_flbAttributesArray[0].updateRange(length);
    this.attributedString_fontArray[0].updateRange(length);
  }

  static default(): TextFlowObject {
    const res = new TextFlowObject(
      guidGenerator(),
      Color.default(),
      0,
      null,
      getCurrentDate(),
      {
        y: 400.0,
        width: 400.0,
        height: 60.0,
        x: 400.0,
      },
      null,
      1,
      null,
      null,
      "text",
      null,
      null,
      0,
      {
        height: 0,
        width: 0,
      },
      null,
      null,
      getCurrentDate(),
      false,
      true,
      Color.default(),
      [
        FontAttrsArray.fromString(
          "Name:HelveticaNeue-CondensedBold|Size:50.000000|Range:{0, 20}"
        ),
      ],
      null,
      Alignment.LEFT,
      {
        fontSize: 50.0,
        fontName: "HelveticaNeue-CondensedBold",
      },
      null,
      "Double Click to Edit",
      [
        FontAttrsArray.fromString(
          "Alignment:0|Color:000000|Name:HelveticaNeue-CondensedBold|Size:50.000000|Range:{0, 20}"
        ),
      ],
    );
    return res;
  }
}

TextFlowObject.displayName = "TextFlowObject";